class RedeleaseShippingCalculator extends HTMLElement {
  static observedAttributes = ['data-cart-signature'];

  connectedCallback() {
    this.form = this.querySelector('[data-shipping-form]');
    this.input = this.querySelector('[data-shipping-postal-code]');
    this.result = this.querySelector('[data-shipping-result]');
    this.button = this.querySelector('button[type="submit"]');
    this.requestId = 0;

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handlePostalCodeChange = this.handlePostalCodeChange.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.handleCartDrawerOpen = this.handleCartDrawerOpen.bind(this);
    this.cartDrawer = this.closest('cart-drawer-component');

    this.form?.addEventListener('submit', this.handleSubmit);
    this.input?.addEventListener('input', this.handleInput);
    document.addEventListener('redelease:postal-code', this.handlePostalCodeChange);
    document.addEventListener('variant:update', this.handleVariantUpdate);
    window.addEventListener('pagehide', this.handlePageHide);
    this.cartDrawer?.addEventListener('dialog:open', this.handleCartDrawerOpen);

    this.updateVisibility();

    if (!this.hidden && !this.hasAttribute('data-cart-context') && this.restorePostalCode()) {
      this.scheduleAutomaticCalculation();
    } else if (this.hasAttribute('data-cart-context')) {
      this.restorePostalCode();
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.input?.removeEventListener('input', this.handleInput);
    document.removeEventListener('redelease:postal-code', this.handlePostalCodeChange);
    document.removeEventListener('variant:update', this.handleVariantUpdate);
    window.removeEventListener('pagehide', this.handlePageHide);
    this.cartDrawer?.removeEventListener('dialog:open', this.handleCartDrawerOpen);
    window.clearTimeout(this.automaticCalculationTimer);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-cart-signature' || oldValue === null || oldValue === newValue || !this.isConnected) return;

    this.resetCartShippingTotals();
    if (this.hasAttribute('data-cart-context')) this.restorePostalCode();
  }

  handleInput() {
    if (!this.input) return;

    const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
    this.input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;

    if (digits.length === 8) {
      this.ignorePostalCodeEvent = true;
      this.savePostalCode(digits);
      this.ignorePostalCodeEvent = false;

    } else if (this.hasAttribute('data-cart-context')) {
      this.resetCartShippingTotals();
    }
  }

  handlePostalCodeChange(event) {
    if (!this.input || document.activeElement === this.input || this.ignorePostalCodeEvent) return;

    const postalCode = event.detail?.postalCode || this.getSavedPostalCode();
    if (postalCode) {
      this.input.value = this.formatPostalCode(postalCode);
      if (!this.hasAttribute('data-cart-context')) this.scheduleAutomaticCalculation();
    }
  }

  handleVariantUpdate(event) {
    if (this.hasAttribute('data-cart-context')) return;

    const detail = event.detail || {};
    const productId = detail.data?.productId;

    if (productId && this.dataset.productId && productId.toString() !== this.dataset.productId.toString()) return;

    if (detail.data?.newProduct?.id) {
      this.dataset.productId = detail.data.newProduct.id.toString();
    }

    if (detail.resource?.id) {
      this.dataset.variantId = detail.resource.id.toString();
      this.dataset.variantAvailable = detail.resource.available ? 'true' : 'false';
      this.dataset.minQuantity = detail.resource.quantity_rule?.min?.toString() || this.dataset.minQuantity || '1';
    } else if (detail.resource === null) {
      this.dataset.variantId = '';
      this.dataset.variantAvailable = 'false';
    }

    this.updateVisibility();
    this.clearResult();

    if (!this.hidden) {
      if (!this.getPostalCode()) this.restorePostalCode();
      if (this.getPostalCode()) this.scheduleAutomaticCalculation();
    }
  }

  handlePageHide() {
    if (!this.temporaryLineKey) return;

    fetch(`${this.getRouteRoot()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: this.temporaryLineKey,
        quantity: 0,
      }),
    }).catch(() => {});
  }

  handleCartDrawerOpen() {
    if (!this.hasAttribute('data-cart-context')) return;
    this.restorePostalCode();
  }

  async handleSubmit(event) {
    event.preventDefault();

    const postalCode = this.getPostalCode();
    if (!postalCode) {
      this.renderMessage('Informe um CEP com 8 dígitos.', true);
      return;
    }

    this.ignorePostalCodeEvent = true;
    this.savePostalCode(postalCode);
    this.ignorePostalCodeEvent = false;

    const isCartContext = this.hasAttribute('data-cart-context');
    const variantId = isCartContext ? null : this.getCurrentVariantId();
    if (!isCartContext && (!variantId || this.dataset.variantAvailable === 'false')) {
      this.renderMessage('Selecione uma opção disponível para calcular o frete.', true);
      return;
    }

    const currentRequestId = ++this.requestId;
    let temporaryLineKey = null;

    this.setLoading(true);
    if (isCartContext) this.resetCartShippingTotals('Calculando...');
    this.clearResult();

    try {
      if (!isCartContext) {
        temporaryLineKey = await this.addTemporaryLine(variantId);
        this.temporaryLineKey = temporaryLineKey;
      }

      if (currentRequestId !== this.requestId) return;

      const province = this.dataset.province || (await this.resolveProvince(postalCode));
      this.markPostalCodeValidated(postalCode);
      const rates = await this.fetchShippingRates(postalCode, province);

      if (currentRequestId !== this.requestId || (!isCartContext && variantId !== this.getCurrentVariantId())) return;

      this.renderRates(rates);
    } catch (error) {
      if (currentRequestId === this.requestId) {
        this.renderMessage(this.getErrorMessage(error), true);
      }
    } finally {
      if (temporaryLineKey) {
        await this.removeTemporaryLine(temporaryLineKey);
        if (this.temporaryLineKey === temporaryLineKey) this.temporaryLineKey = null;
      }

      if (currentRequestId === this.requestId) {
        this.setLoading(false);
      }
    }
  }

  getPostalCode() {
    const digits = this.input?.value.replace(/\D/g, '') || '';
    return digits.length === 8 ? digits : '';
  }

  restorePostalCode() {
    const postalCode = this.getSavedPostalCode();
    if (this.input && postalCode.length === 8) {
      this.input.value = this.formatPostalCode(postalCode);
      return true;
    }

    return false;
  }

  scheduleAutomaticCalculation() {
    window.clearTimeout(this.automaticCalculationTimer);
    this.automaticCalculationTimer = window.setTimeout(() => {
      if (
        this.hidden ||
        !this.isConnected ||
        !this.form ||
        !this.getPostalCode() ||
        this.button?.disabled ||
        (this.hasAttribute('data-cart-context') && !this.cartDrawer?.querySelector('dialog[open]'))
      )
        return;
      this.form.requestSubmit();
    }, 150);
  }

  updateVisibility() {
    if (this.hasAttribute('data-cart-context')) {
      this.hidden = false;
      return;
    }

    const isUnavailable = !this.dataset.variantId || this.dataset.variantAvailable === 'false';
    this.hidden = isUnavailable;
  }

  getSavedPostalCode() {
    if (window.RedeleaseDelivery?.getPostalCode) {
      return window.RedeleaseDelivery.getPostalCode();
    }

    try {
      return (window.localStorage.getItem('redelease_postal_code') || '').replace(/\D/g, '').slice(0, 8);
    } catch (error) {
      return '';
    }
  }

  savePostalCode(postalCode) {
    if (window.RedeleaseDelivery?.savePostalCode) {
      window.RedeleaseDelivery.savePostalCode(postalCode);
      return;
    }

    try {
      window.localStorage.setItem('redelease_postal_code', postalCode);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }

  markPostalCodeValidated(postalCode) {
    if (window.RedeleaseDelivery?.markPostalCodeValidated) {
      window.RedeleaseDelivery.markPostalCodeValidated(postalCode);
      return;
    }

    try {
      window.localStorage.setItem('redelease_validated_postal_code', postalCode);
      document.dispatchEvent(new CustomEvent('redelease:postal-code-validated', { detail: { postalCode } }));
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }

  formatPostalCode(postalCode) {
    const digits = (postalCode || '').replace(/\D/g, '').slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  getCurrentVariantId() {
    const variantInput = this.closest('.product-details')?.querySelector('product-form-component input[name="id"]');
    return variantInput?.value || this.dataset.variantId || new URL(window.location.href).searchParams.get('variant');
  }

  getQuantity() {
    const quantityInput = this.closest('.product-details')?.querySelector('product-form-component input[name="quantity"]');
    const minQuantity = Number.parseInt(this.dataset.minQuantity || '1', 10);
    const quantity = Number.parseInt(quantityInput?.value || minQuantity.toString(), 10);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : Math.max(minQuantity, 1);
  }

  async addTemporaryLine(variantId) {
    const token = `redelease-shipping-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const response = await fetch(`${this.getRouteRoot()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: this.getQuantity(),
            properties: {
              _redelease_shipping_estimate: token,
            },
          },
        ],
      }),
    });

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new Error(payload?.description || payload?.message || 'Não foi possível preparar o produto para consulta.');
    }

    const temporaryLine = payload?.items?.find(
      (item) => item.properties?._redelease_shipping_estimate === token
    );

    if (!temporaryLine?.key) {
      throw new Error('Não foi possível localizar a consulta temporária no carrinho.');
    }

    return temporaryLine.key;
  }

  async resolveProvince(postalCode) {
    const response = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Não foi possível identificar o estado deste CEP. Tente novamente.');
    }

    const payload = await this.parseJson(response);
    const province = payload?.uf?.toString().toUpperCase();

    if (payload?.erro || !province || !/^[A-Z]{2}$/.test(province)) {
      throw new Error('CEP não encontrado. Confira os números informados.');
    }

    return province;
  }

  async fetchShippingRates(postalCode, province) {
    const params = this.buildShippingParams(postalCode, province);
    const root = this.getRouteRoot();
    const prepareResponse = await fetch(`${root}cart/prepare_shipping_rates.json?${params.toString()}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!prepareResponse.ok && prepareResponse.status !== 202) {
      const payload = await this.parseJson(prepareResponse);
      throw new Error(this.normalizeShopifyError(payload) || 'Não foi possível iniciar o cálculo de frete.');
    }

    for (let attempt = 0; attempt < 8; attempt += 1) {
      await this.wait(700 + attempt * 350);

      const response = await fetch(`${root}cart/async_shipping_rates.json?${params.toString()}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        const payload = await this.parseJson(response);
        throw new Error(this.normalizeShopifyError(payload) || 'Não foi possível calcular o frete para este CEP.');
      }

      const payload = await this.parseJson(response);
      if (payload?.shipping_rates) return payload.shipping_rates;
    }

    throw new Error('O cálculo de frete demorou mais que o esperado. Tente novamente.');
  }

  buildShippingParams(postalCode, province) {
    const params = new URLSearchParams();
    params.append('shipping_address[zip]', postalCode);
    params.append('shipping_address[country]', this.dataset.country || 'Brazil');

    if (province) {
      params.append('shipping_address[province]', province);
    }

    return params;
  }

  async removeTemporaryLine(lineKey) {
    try {
      const response = await fetch(`${this.getRouteRoot()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      console.error('Redelease shipping estimate cleanup failed', error);
    }
  }

  renderRates(rates) {
    if (!this.result) return;

    if (!Array.isArray(rates) || rates.length === 0) {
      this.renderMessage('Não encontramos opções de entrega para este CEP.', true);
      return;
    }

    const sortedRates = [...rates].sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    if (this.hasAttribute('data-cart-context')) {
      this.applyCartShippingRate(sortedRates[0]);
      this.clearResult();
      return;
    }

    const items = sortedRates
      .map((rate) => {
        const name = this.escapeHtml(rate.presentment_name || rate.name || 'Entrega');
        const meta = this.getRateMeta(rate);
        const price = this.escapeHtml(this.formatRatePrice(rate));

        return `
          <li class="redelease-shipping-calculator__rate">
            <span class="redelease-shipping-calculator__rate-name">
              ${name}
              ${meta ? `<span class="redelease-shipping-calculator__rate-meta">${this.escapeHtml(meta)}</span>` : ''}
            </span>
            <span class="redelease-shipping-calculator__rate-price">${price}</span>
          </li>
        `;
      })
      .join('');

    this.result.innerHTML = `<ul class="redelease-shipping-calculator__rates">${items}</ul>`;
    this.updateCartDrawerStickyState();
  }

  renderMessage(message, isError = false) {
    if (!this.result) return;

    const className = isError
      ? 'redelease-shipping-calculator__message redelease-shipping-calculator__message--error'
      : 'redelease-shipping-calculator__message';

    this.result.innerHTML = `<p class="${className}">${this.escapeHtml(message)}</p>`;
    if (isError && this.hasAttribute('data-cart-context')) this.resetCartShippingTotals();
    this.updateCartDrawerStickyState();
  }

  applyCartShippingRate(rate) {
    const totals = this.closest('.cart-drawer__summary')?.querySelector('[data-redelease-cart-shipping-totals]');
    if (!totals || !rate) return;

    const shippingPrice = Number(rate.price || 0);
    const shippingInCents = Math.round(shippingPrice * 100);
    const baseTotal = Number(totals.dataset.cartBaseTotal || 0);
    const shippingElement = totals.querySelector('[data-redelease-cart-shipping]');
    const estimatedTotalElement = this.closest('.cart-drawer__summary')?.querySelector(
      '[data-redelease-cart-estimated-total]'
    );

    if (shippingElement) shippingElement.textContent = shippingPrice === 0 ? 'Grátis' : this.formatMoney(shippingInCents, totals);
    if (estimatedTotalElement) {
      estimatedTotalElement.textContent = this.formatMoney(baseTotal + shippingInCents, totals);
      estimatedTotalElement.setAttribute('value', estimatedTotalElement.textContent);
    }
  }

  resetCartShippingTotals(label = 'A calcular') {
    if (!this.hasAttribute('data-cart-context')) return;
    const summary = this.closest('.cart-drawer__summary');
    const totals = summary?.querySelector('[data-redelease-cart-shipping-totals]');
    if (!totals) return;

    const shippingElement = totals.querySelector('[data-redelease-cart-shipping]');
    const estimatedTotalElement = summary.querySelector('[data-redelease-cart-estimated-total]');
    if (shippingElement) shippingElement.textContent = label;
    if (estimatedTotalElement) {
      estimatedTotalElement.textContent = this.formatMoney(Number(totals.dataset.cartBaseTotal || 0), totals);
      estimatedTotalElement.setAttribute('value', estimatedTotalElement.textContent);
    }
  }

  formatMoney(cents, totals) {
    try {
      return new Intl.NumberFormat(totals?.dataset.cartLocale || 'pt-BR', {
        style: 'currency',
        currency: totals?.dataset.cartCurrency || 'BRL',
      }).format(cents / 100);
    } catch (error) {
      return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  clearResult() {
    if (this.result) {
      this.result.innerHTML = '';
      this.updateCartDrawerStickyState();
    }
  }

  updateCartDrawerStickyState() {
    const dialog = this.closest('.cart-drawer__dialog');
    const summary = this.closest('.cart-drawer__summary');
    if (!dialog || !summary) return;

    window.requestAnimationFrame(() => {
      if (!dialog.isConnected || !summary.isConnected) return;
      const ratio = summary.getBoundingClientRect().height / dialog.getBoundingClientRect().height;
      dialog.setAttribute('cart-summary-sticky', ratio > 0.5 ? 'false' : 'true');
    });
  }

  setLoading(isLoading) {
    if (!this.button || !this.input) return;

    this.button.disabled = isLoading;
    this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    this.input.disabled = isLoading;
  }

  getRateMeta(rate) {
    let deliveryDays = 0;
    if (Array.isArray(rate.delivery_days) && rate.delivery_days.length > 0) {
      const days = rate.delivery_days.map(Number).filter(Number.isFinite);
      deliveryDays = days.length ? Math.max(...days) : 0;
    } else if (Array.isArray(rate.delivery_range) && rate.delivery_range.length > 0) {
      deliveryDays = this.countBusinessDaysUntil(rate.delivery_range[rate.delivery_range.length - 1]);
    } else if (rate.delivery_date) {
      deliveryDays = this.countBusinessDaysUntil(rate.delivery_date);
    }

    if (deliveryDays > 0) {
      const dayLabel = deliveryDays === 1 ? 'dia útil' : 'dias úteis';
      return `Entrega em ${deliveryDays} ${dayLabel} a partir da data de postagem.`;
    }

    return rate.description || '';
  }

  countBusinessDaysUntil(dateValue) {
    const targetDate = new Date(`${dateValue}T12:00:00`);
    if (Number.isNaN(targetDate.getTime())) return 0;

    const currentDate = new Date();
    currentDate.setHours(12, 0, 0, 0);
    targetDate.setHours(12, 0, 0, 0);

    let businessDays = 0;
    const cursor = new Date(currentDate);

    while (cursor < targetDate) {
      cursor.setDate(cursor.getDate() + 1);
      const dayOfWeek = cursor.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) businessDays += 1;
    }

    return businessDays;
  }

  formatRatePrice(rate) {
    const price = Number(rate.price || 0);
    const currency = rate.currency || window.Shopify?.currency?.active || 'BRL';

    if (price === 0) return 'Grátis';

    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
      }).format(price);
    } catch (error) {
      return `R$ ${price.toFixed(2).replace('.', ',')}`;
    }
  }

  getErrorMessage(error) {
    return error?.message || 'Não foi possível calcular o frete agora. Tente novamente.';
  }

  normalizeShopifyError(payload) {
    if (!payload) return '';
    if (Array.isArray(payload.error)) return payload.error.join(' ');
    if (typeof payload.error === 'string') return payload.error;
    if (typeof payload.description === 'string') return payload.description;
    if (typeof payload.message === 'string') return payload.message;

    if (typeof payload === 'object') {
      return Object.values(payload)
        .flat()
        .filter((value) => typeof value === 'string')
        .join(' ');
    }

    return '';
  }

  async parseJson(response) {
    const text = await response.text();
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  getRouteRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  wait(delay) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, delay);
    });
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : value.toString();
    return div.innerHTML;
  }
}

if (!customElements.get('redelease-shipping-calculator')) {
  customElements.define('redelease-shipping-calculator', RedeleaseShippingCalculator);
}
